import React, { useState, useEffect } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { dataService } from "../services/dataService";
import { useOrder } from "../context/OrderContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import FloatingLabelInput from "../components/form_elements/FloatingLabelInput";
import { showErrorToast } from "../components/toasts/ToastService";
import { useOrderSave } from "../context/OrderSaveContext";
import ShoeLoader from "../components/loaders/ShoeLoader";

const ModellValaszto = () => {
	const { order, updateOrder, resetModelDefaults } = useOrder();
	const navigate = useStepNavigate();
	const [modellNev, setModellNev] = useState("");
	const [modelData, setModelData] = useState(null);
	const [reasons, setReasons] = useState(null);
	const [modelStatus, setModelStatus] = useState(null);
	const [loading, setLoading] = useState(false);
	const [saveLoading, setSaveLoading] = useState(false);
	const [imageSrc, setImageSrc] = useState(null);
	const [imageLoading, setImageLoading] = useState(false);
	const { saveOrder } = useOrderSave();
	const [searched, setSearched] = useState(false);

	const handleInputChange = (e) => {
		setModellNev(e.target.value);
		setModelData(null);
		setSearched(false);
	};

	const handleSearch = async () => {
		if (!modellNev.trim()) {
			showErrorToast("Kérem adjon meg egy modell nevet!");
			return;
		}

		try {
			setLoading(true);
			setSearched(true);
			const response = await dataService.getShoeModelForOrder({
				model_code: modellNev.trim(),
				hmeliorder_szam: order.hmeliorder.hmeliorder_szam,
				partner_szam: order.partner.partner_szam,
				localstorage: order
			});

			if (response.data?.status === "ok") {
				const model_status = response.data.data?.model_status || 'error';
				setModelStatus(model_status);
				
				if(model_status == 'error') {
					const reasons_data = response.data.data?.reasons || [];
					setReasons(reasons_data);
					setModelData(null);
					showErrorToast("A modell nem elérhető.");
				}else{
					const model_data = response.data.data?.models[0] || {};
					setReasons(null);
					setModelData(model_data);
					loadImage(model_data.model_name);

					const updatedOrder = {
						...order,
						model_options: {
							corner_types_for_model: response.data.data.model_options.corner_types_for_model || [],
							corner_heights_for_model: response.data.data.model_options.corner_heights_for_model || [],
							shoe_horn_models_for_model: response.data.data.model_options.shoe_horn_models_for_model || [],
							linermaterials_for_model: response.data.data.model_options.linermaterials_for_model || [],
							colors_and_materials_for_model: response.data.data.model_options.colors_and_materials_for_model || [],
							mounting_types_for_model: response.data.data.model_options.mounting_types_for_model || [],
							special_needs_for_model: response.data.data.model_options.special_needs_for_model || [],
							max_stem_height: response.data.data.model_options.max_stem_height || null,
							is_open_nosed: response.data.data.model_options.is_open_nosed || null
						}
					};
					updateOrder(updatedOrder);
				}
				
			} else {
				showErrorToast("Hiba a lekérésben! Próbálja újra." + (response.data?.message || ""));
				setModelData(null);
				setModelStatus(null);
				setImageSrc(null);
			}
		} catch (error) {
			showErrorToast("Hiba történt a modellek lekérésekor.");
			setModelData(null);
			setImageSrc(null);
		} finally {
			setLoading(false);
		}
	};

	
	const loadImage = async (modelName) => {
		setImageLoading(true);
		if (!modelName) {
			setImageSrc("/images/models/model_placeholder.png");
			setImageLoading(false);
			return;
		}
		const modelImagePath = `/images/models/${modelName}.png`;
		const placeholderPath = "/images/models/model_placeholder.png";
		try {
			const img = new Image();
			img.onload = () => {
				setImageSrc(modelImagePath);
				setImageLoading(false);
			};
			img.onerror = () => {
				setImageSrc(placeholderPath);
				setImageLoading(false);
			};
			img.src = modelImagePath;
		} catch (error) {
			console.error("Hiba a kép betöltésekor:", error);
			setImageSrc(placeholderPath);
			setImageLoading(false);
		}
	};


	useEffect(() => {
		if (modelData?.model_name) {
			loadImage(modelData.model_name);
		}
	}, [modelData]);

	// Automatikus betöltés, ha van mentett modell név
	useEffect(() => {
		const savedModelName = order.hmeliorder?.plus_hmeliorder_real_model_name;
		if (savedModelName && savedModelName.trim() && !modellNev && !searched) {
			setModellNev(savedModelName);
			// Automatikus keresés futtatása egy kis késleltetéssel
			setTimeout(() => {
				if (savedModelName.trim()) {
					handleSearchWithModelName(savedModelName);
				}
			}, 100);
		}
	}, [order.hmeliorder?.plus_hmeliorder_real_model_name]);

	// Külön függvény az automatikus kereséshez
	const handleSearchWithModelName = async (modelName) => {
		try {
			setLoading(true);
			setSearched(true);
			const response = await dataService.getShoeModelForOrder({ 
				model_code: modelName.trim(), 
				hmeliorder_szam: order.hmeliorder.hmeliorder_szam, 
				partner_szam: order.partner.partner_szam, 
				localstorage: order 
			});

			if (response.data?.status === "ok") {
				const model_status = response.data.data?.model_status || 'error';
				setModelStatus(model_status);
				if(model_status == 'error') {
					const reasons_data = response.data.data?.reasons || [];
					setReasons(reasons_data);
					setModelData(null);
				}else{
					const model_data = response.data.data?.models[0] || {};
					setReasons(null);
					setModelData(model_data);
					loadImage(model_data.model_name);

					const updatedOrder = {
						...order,
						model_options: {
							corner_types_for_model: response.data.data.model_options.corner_types_for_model || [],
							corner_heights_for_model: response.data.data.model_options.corner_heights_for_model || [],
							shoe_horn_models_for_model: response.data.data.model_options.shoe_horn_models_for_model || [],
							linermaterials_for_model: response.data.data.model_options.linermaterials_for_model || [],
							colors_and_materials_for_model: response.data.data.model_options.colors_and_materials_for_model || [],
							mounting_types_for_model: response.data.data.model_options.mounting_types_for_model || [],
							special_needs_for_model: response.data.data.model_options.special_needs_for_model || [],
							max_stem_height: response.data.data.model_options.max_stem_height || null,
							is_open_nosed: response.data.data.model_options.is_open_nosed || null
						}
					};
					updateOrder(updatedOrder);
				}
			} else {
				setModelData(null);
				setModelStatus(null);
				setImageSrc(null);
			}
		} catch (error) {
			console.error("Hiba az automatikus modell kereséskor:", error);
			setModelData(null);
			setImageSrc(null);
		} finally {
			setLoading(false);
		}
	};


	const handleValasztas = async () => {
		if (!modelData) {
			showErrorToast("Kérem, válasszon érvényes modellt!");
			return;
		}
		try {
			console.log("Modell kiválasztva:", modelData);
			console.log("mentés ... ");
			setSaveLoading(true);
			const nextModelId = Number(modelData.model_szam);
			const previouslySelectedId = order.hmeliorder.hmeliorder_model_id;
			const previouslySelectedName = order.hmeliorder.plus_hmeliorder_real_model_name;
			const hasPreviousId = previouslySelectedId !== null && previouslySelectedId !== undefined;
			const hasPreviousName = Boolean(previouslySelectedName);
			const isModelChange =
				(hasPreviousId && previouslySelectedId !== nextModelId) ||
				(hasPreviousName && previouslySelectedName !== modelData.model_name);
			const workingOrder = isModelChange ? resetModelDefaults() : order;

			const updatedOrder = {
				...workingOrder,
				hmeliorder: {
					...workingOrder.hmeliorder,
					hmeliorder_modell_num: modelData.model_name, // vigyázz, a mmelimodel.name van felhasználva mint code!!!!
					hmeliorder_model_id: nextModelId,
					hmeliorder_object: Number(modelData.model_headobject_id),
					plus_hmeliorder_real_model_name: modelData.model_name
				}
			};
			updateOrder(updatedOrder);
			const result = await saveOrder(updatedOrder);
			if (result.success) {
				navigate("/RogzitesTipus");
			}
		} catch (error) {
			console.error("Hiba a rendelés mentésekor:", error);
		} finally {
			setSaveLoading(false);
		}
	};

	const model_status_ok =  modelStatus === 'ok';

	return (
		<TablerLayout title="Írja be a mezőbe a választott modell nevét, kódját!">
			<div className="row justify-content-center">
				<div className="col-md-10 mb-4">
					<div className="card-body">
						<div className="mb-3 d-flex flex-column flex-md-row gap-2 align-items-start align-items-md-center">
							<FloatingLabelInput
								type="text"
								inputMode="numeric"
								label="Modell *"
								value={modellNev}
								onChange={handleInputChange}
								disabled={loading || saveLoading}
								required
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										handleSearch();
									}
								}}
								className="form-control form-control-lg w-100"
							/>

							<button onClick={handleSearch} disabled={loading || saveLoading} className="btn btn-primary btn-md mt-2 mt-md-0">
								{loading ? "Keresés..." : "Keresés"}
							</button>

							{/* Develop gombok */}
							{process.env.NODE_ENV === 'development' && (
								<div className="d-flex gap-2 flex-wrap mt-2 mt-md-0">
									<button
										type="button"
										className="btn btn-warning btn-sm"
										onClick={() => setModellNev("7503")}
									>
										🧪 Félcipő: 7503
									</button>
									<button
										type="button"
										className="btn btn-warning btn-sm"
										onClick={() => setModellNev("7117")}
									>
										🧪 Magasszárú: 7117
									</button>
								</div>
							)}
						</div>

						{/* Találati adatok */}
						<div className="mb-4">
							
							{loading && searched && (
								<div className="alert alert-warning d-flex flex-column align-items-center" style={{ textAlign: "center", margin: " 0" }}>
									<div>Modell keresése folyamatban ...</div>
									<div style={{ textAlign: "center", margin: " 0" }}>
										<ShoeLoader text={false} />
									</div>
								</div>
							)}

							{/* Sikeres találat */}
							{!loading && searched && modelData && (
								<div className={`alert ${model_status_ok ? "alert-success" : "alert-warning"}`}>
									<b>Talált modell:</b> {modelData.model_name}<br />
									<span className="text-muted">{modelData.elishoetype_national || "-"}</span>
									<br />
									<span className="text-muted">{modelData.model_code || "-"}</span>
									<br />
									<span className="text-muted">{modelData.elishoemodel_national || "-"}</span>
									{!model_status_ok && <div className="text-danger fw-bold">Ez a modell nem felel meg a kiválasztott cipő típusnak!</div>}
								</div>
							)}
							
							{/* Hibaüzenetek megjelenítése, ha modelStatus error */}
							{!loading && searched && modelStatus === 'error' && (
								<div className="alert alert-danger">
									<div className="d-flex align-items-start mb-2">
										<i className="fas fa-exclamation-triangle me-2 mt-1 text-danger"></i>
										<div>
											<h6 className="alert-heading mb-2">A modell nem választható a következő ok(ok) miatt:</h6>
											{modelStatus === 'error' && reasons && reasons.length > 0 && (
											<div className="mb-0">
												{reasons.map((reason, index) => (
													<div key={index} className="mb-1">
														<i className="fas fa-times-circle me-1 text-danger"></i>
													   - {reason}
													</div>
												))}
											</div>
											)}
										</div>
									</div>
								</div>
							)}
							
							{/* Nincs találat */}
							{!loading && searched && modelData === null && modellNev && modelStatus !== 'error' && (
								<div className="alert alert-danger">
									Nincs ilyen modell: <b>{modellNev}</b>
								</div>
							)}
						</div>
						
						{/* Modellábrázolás */}
						{!loading && modelData && (
							<div className="card text-center mb-4">
								<div className="card-body">
									{imageLoading ? (
										<div className="d-flex justify-content-center align-items-center" style={{ height: "250px" }}>
											<div className="spinner-border" role="status">
												<span className="visually-hidden">Kép betöltése...</span>
											</div>
										</div>
									) : (
										<img 
											src={imageSrc} 
											alt={modelData.model_name ? `${modelData.model_name} modell` : "Modell helyőrző"} 
											style={{ 
												maxWidth: "250px", 
												maxHeight: "250px",
												objectFit: "contain"
											}} 
											onError={(e) => {
												e.target.src = require("../components/images/model_placeholder.png");
											}}
										/>
									)}
								</div>
							</div>
						)}
					</div>

					
				</div>
			</div>
			<div className="card-footer d-flex justify-content-between">
						<StepperNavButton side="left" label="Vissza" onClick={() => navigate("/CipoForma")} disabled={loading || saveLoading} />
						<StepperNavButton side="right" label="Kiválasztás" onClick={handleValasztas} disabled={!modelData || !model_status_ok || loading || saveLoading} />
					</div>
		</TablerLayout>
	);
};

export default ModellValaszto;
